import {tokensForThemeProvider} from '@forecast-it/design-system';
import React, {useEffect, useRef} from 'react';
import {useIntl} from 'react-intl';
import styled, {css, keyframes} from 'styled-components';
import {dispatch, EVENT_ID} from '../../../../containers/event_manager';
import type {ControlState, DisabledReason} from './controlState';
import {anchorElementId, novaInstanceKey} from './NovaAgentPicker';

export interface InvocationControlProps {
	taskId: string;
	// See NovaAgentPickerProps.instanceKey (NovaAgentPicker.tsx) — must match whatever the
	// sibling agentPicker below was given, or the caret's id and the picker's toggle/lookup key
	// disagree and the picker can't find its own caret.
	instanceKey?: string;
	state: ControlState;
	accountLimitNotice?: 'near_limit' | 'at_limit';
	onAction: () => void;
	onOpenOutput: () => void;
	agentPicker?: React.ReactNode; // rendered inline below the control when the picker (a later task) is mounted; undefined until then
}

const REASON_MESSAGE_ID: Record<DisabledReason, string> = {
	role: 'nova_invocation.control.reason_role',
	task_locked: 'nova_invocation.control.reason_task_locked',
	no_agent_for_role: 'nova_invocation.control.reason_no_agent_for_role',
	no_genai_access: 'nova_invocation.control.reason_no_genai_access',
	run_cap_reached: 'nova_invocation.control.reason_run_cap',
};

let disabledReasonIdCounter = 0;

function useDisabledReasonId(): string {
	const idRef = useRef<string>();
	if (!idRef.current) {
		disabledReasonIdCounter += 1;
		idRef.current = `nova-disabled-reason-${disabledReasonIdCounter}`;
	}
	return idRef.current;
}

// This control is a small tinted status pill (e.g. light-blue "Action", light-green pulsing
// "Ready"), not a standard filled/outlined button — no design-system Button emphasis produces
// that look, so it's built here from design tokens rather than composed from Button.
type ChipTone = 'blue' | 'green' | 'neutral';

// Optional chaining because this module loads transitively through the shared web-components
// barrel — unrelated test files that mock '@forecast-it/design-system' without providing
// tokensForThemeProvider would otherwise crash on import alone, before any test even runs.
const ref = tokensForThemeProvider?.sky?.ref?.color;
const sys = tokensForThemeProvider?.sky?.sys?.color;

const CHIP_TONE_COLORS: Record<ChipTone, {background: string; border: string; text: string}> = {
	blue: {background: ref?.blue['50'] ?? '', border: ref?.blue['100'] ?? '', text: ref?.blue['900'] ?? ''},
	green: {background: ref?.green['50'] ?? '', border: ref?.green['75'] ?? '', text: sys?.text.semantic.success ?? ''},
	neutral: {
		background: ref?.neutral['200'] ?? '',
		border: ref?.neutral['300'] ?? '',
		text: ref?.neutral['400'] ?? '',
	},
};

const pulse = keyframes`
	0%, 100% { opacity: 1; }
	50% { opacity: 0.45; }
`;

const spin = keyframes`
	to { transform: rotate(360deg); }
`;

const ChipButton = styled.button<{$tone: ChipTone; $pulse: boolean}>`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	border-radius: 999px;
	font-family: inherit;
	font-size: 12px;
	line-height: 16px;
	font-weight: 500;
	padding: 5px 11px;
	border: 1px solid ${({$tone}) => CHIP_TONE_COLORS[$tone].border};
	background: ${({$tone}) => CHIP_TONE_COLORS[$tone].background};
	color: ${({$tone}) => CHIP_TONE_COLORS[$tone].text};
	cursor: pointer;
	${({$pulse}) =>
		$pulse &&
		css`
			animation: ${pulse} 2s infinite;
		`}
	&:disabled {
		cursor: not-allowed;
	}
`;

// Reuses CHIP_TONE_COLORS.blue rather than reading ref.blue[...] directly — ref itself can be
// undefined (see the optional chaining above), and CHIP_TONE_COLORS already guards that.
const Spinner = styled.span`
	display: inline-block;
	flex: none;
	width: 12px;
	height: 12px;
	border-radius: 50%;
	border: 2px solid ${CHIP_TONE_COLORS.blue.border};
	border-top-color: ${CHIP_TONE_COLORS.blue.text};
	animation: ${spin} 0.7s linear infinite;
`;

const CaretButton = styled.button`
	display: inline-flex;
	flex: none;
	align-items: center;
	justify-content: center;
	width: 22px;
	height: 22px;
	padding: 0;
	border-radius: 999px;
	border: 1px solid ${CHIP_TONE_COLORS.blue.border};
	background: ${CHIP_TONE_COLORS.blue.background};
	color: ${CHIP_TONE_COLORS.blue.text};
	font-size: 10px;
	line-height: 14px;
	cursor: pointer;
`;

const ChipRow = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 4px;
`;

// This region exists only so a screen reader announces the working transition (see the focus
// effect below) — it must never be visible. Standard "visually hidden" clip, not display:none,
// so it stays in the accessibility tree while taking no visual space.
const StatusRegion = styled.span`
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
`;

export const NovaInvocationControl = (props: InvocationControlProps): JSX.Element => {
	const {state} = props;
	const {formatMessage} = useIntl();
	const disabledReasonId = useDisabledReasonId();
	const statusRef = useRef<HTMLSpanElement>(null);
	const previousKindRef = useRef(state.kind);

	useEffect(() => {
		// Clicking "Action" hands focus to a button that immediately goes disabled on the
		// working transition; browsers blur a disabled focused element, dropping a keyboard
		// user's place with no announcement. Moving focus to the status region keeps it local.
		if (previousKindRef.current === 'action' && state.kind === 'working') {
			statusRef.current?.focus();
		}
		previousKindRef.current = state.kind;
	});

	const limitBanner = props.accountLimitNotice ? (
		<span data-cy="nova-limit-notice">
			{props.accountLimitNotice === 'at_limit'
				? formatMessage({id: 'nova_invocation.control.limit_at'})
				: formatMessage({id: 'nova_invocation.control.limit_near'})}
		</span>
	) : null;

	let button: React.ReactNode;
	let statusText = '';
	let disabledReasonSpan: React.ReactNode = null;
	let showLimitBanner = true;
	let showCaret = false;

	switch (state.kind) {
		case 'disabled':
			button = (
				<ChipButton
					$tone="neutral"
					$pulse={false}
					disabled
					aria-describedby={disabledReasonId}
					data-cy="nova-invocation-control"
				>
					{formatMessage({id: 'nova_invocation.control.invoke'})}
				</ChipButton>
			);
			// Visually hidden, same as StatusRegion below — aria-describedby (on the button above)
			// reads this for a screen reader regardless of visibility. Left as plain, unclipped
			// text it rendered as a literal sentence next to the disabled pill inside the row's
			// fixed-width Nova column, wrapping/overflowing on every read-only task.
			disabledReasonSpan = (
				<StatusRegion id={disabledReasonId}>{formatMessage({id: REASON_MESSAGE_ID[state.reason]})}</StatusRegion>
			);
			// Already blocked for a specific eligibility reason — an account-limit notice on top would be redundant.
			showLimitBanner = false;
			break;
		case 'working':
			button = (
				<ChipButton $tone="blue" $pulse={false} disabled data-cy="nova-invocation-control">
					<Spinner />
					{formatMessage({id: 'nova_invocation.control.working'})}
				</ChipButton>
			);
			statusText = formatMessage({id: 'nova_invocation.control.status_working'});
			break;
		case 'ready_or_continue':
			button = (
				<ChipButton
					$tone={state.read ? 'blue' : 'green'}
					$pulse={!state.read}
					onClick={props.onOpenOutput}
					data-cy="nova-invocation-control"
				>
					{formatMessage({id: state.read ? 'nova_invocation.control.continue' : 'nova_invocation.control.ready'})}
				</ChipButton>
			);
			break;
		case 'action':
			button = (
				<ChipButton $tone="blue" $pulse={false} onClick={props.onAction} data-cy="nova-invocation-control">
					{formatMessage({id: 'nova_invocation.control.invoke'})}
				</ChipButton>
			);
			showCaret = true;
			break;
		default: {
			const _exhaustive: never = state;
			void _exhaustive;
			button = (
				<ChipButton $tone="neutral" $pulse={false} disabled data-cy="nova-invocation-control">
					{formatMessage({id: 'nova_invocation.control.invoke'})}
				</ChipButton>
			);
		}
	}

	return (
		// position: relative so `agentPicker` (a later task's popover) has a defined anchor to
		// position:absolute against, regardless of whatever container each mounting surface
		// happens to wrap this control in.
		<span style={{position: 'relative', display: 'inline-flex', alignItems: 'center'}}>
			<ChipRow>
				{button}
				{showCaret && (
					<CaretButton
						aria-label={formatMessage({id: 'nova_invocation.control.choose_agent'})}
						onClick={() => dispatch(EVENT_ID.NOVA_TOGGLE_PICKER, novaInstanceKey(props.taskId, props.instanceKey))}
						data-cy="nova-invocation-caret"
						id={anchorElementId(props.taskId, props.instanceKey)}
					>
						▾
					</CaretButton>
				)}
			</ChipRow>
			<StatusRegion role="status" ref={statusRef} tabIndex={-1}>
				{statusText}
			</StatusRegion>
			{disabledReasonSpan}
			{state.kind === 'action' && props.agentPicker}
			{showLimitBanner && limitBanner}
		</span>
	);
};
